import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, CheckCircle2, Circle, PlayCircle, Settings } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useLessonStore } from '@/store/lessonStore';
import { course } from '@/data/index';
import { ProgressManager } from '@/components/lesson/ProgressManager';

/**
 * Sidebar компонент с навигацией по модулям и урокам
 * Интегрирован с lessonStore для отображения реального прогресса
 */
export function Sidebar() {
  const location = useLocation();
  const { isLessonCompleted, getCompletedLessonsCount, getOverallProgress } = useLessonStore();
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set(['1']));
  const [isVisible, setIsVisible] = useState(false);

  // Анимация появления при монтировании
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Вычисляем прогресс из store
  const totalLessons = useMemo(() => {
    return course.modules.reduce((acc, module) => acc + module.lessons.length, 0);
  }, []);

  const completedLessonsCount = getCompletedLessonsCount();
  const progressPercent = getOverallProgress();

  const currentLessonId = location.pathname.split('/').pop() ?? '1-1';

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const getLessonIcon = (lessonId: string, isCurrent: boolean) => {
    const isCompleted = isLessonCompleted(lessonId);

    if (isCompleted) {
      return <CheckCircle2 className="h-4 w-4 text-git-orange animate-checkmark-appear" />;
    }
    if (isCurrent) {
      return <PlayCircle className="h-4 w-4 text-git-orange" />;
    }
    return <Circle className="h-4 w-4 text-muted-foreground" />;
  };

  // Получить количество завершённых уроков в модуле
  const getModuleCompletedCount = (moduleLessons: Array<{ id: string }>) => {
    return moduleLessons.filter((lesson) => isLessonCompleted(lesson.id)).length;
  };

  return (
    <aside
      className={cn(
        'flex h-screen w-72 flex-col border-r border-sidebar-border bg-sidebar',
        'transition-[opacity,transform] duration-300',
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
      )}
    >
      {/* Header */}
      <div className="border-b border-sidebar-border p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-git-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M23.546 10.93L13.067.452c-.604-.603-1.582-.603-2.188 0L8.708 2.627l2.76 2.76c.645-.215 1.379-.07 1.889.441.516.515.658 1.258.438 1.9l2.658 2.66c.645-.223 1.387-.078 1.9.435.721.72.721 1.884 0 2.604-.719.719-1.881.719-2.6 0-.539-.541-.674-1.337-.404-1.996L12.86 8.955v6.525c.176.086.342.203.488.348.713.721.713 1.883 0 2.6-.719.721-1.889.721-2.609 0-.719-.719-.719-1.879 0-2.598.182-.184.387-.317.605-.406V8.835c-.217-.091-.424-.222-.6-.401-.545-.545-.676-1.342-.396-2.009L7.636 3.7.45 10.881c-.6.605-.6 1.584 0 2.189l10.48 10.477c.604.604 1.582.604 2.186 0l10.43-10.43c.605-.603.605-1.582 0-2.187" />
            </svg>
          </div>
          <div>
            <h1 className="font-semibold">Git Mastery</h1>
            <p className="text-sm text-muted-foreground">Interactive Course</p>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Course Progress</span>
            <span className="font-medium">
              {completedLessonsCount}/{totalLessons}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" showPulse animateComplete />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-4">
        <nav className="space-y-1 stagger-animation">
          {course.modules.map((module, moduleIndex) => (
            <div
              key={module.id}
              className={cn(
                'transition-[opacity,transform] duration-300',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              )}
              style={{ transitionDelay: `${moduleIndex * 50}ms` }}
            >
              {/* Module header */}
              <button
                onClick={() => toggleModule(module.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left',
                  'transition-[background-color] duration-200 hover:bg-sidebar-accent'
                )}
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200',
                    !expandedModules.has(module.id) && '-rotate-90'
                  )}
                />
                <span className="flex-1 text-sm font-medium">{module.title}</span>
                <span className="text-xs text-muted-foreground">
                  {getModuleCompletedCount(module.lessons)}/{module.lessons.length}
                </span>
              </button>

              {/* Lessons */}
              <div
                className={cn(
                  'overflow-hidden transition-[max-height,opacity] duration-300 ease-out',
                  expandedModules.has(module.id)
                    ? 'max-h-[1000px] opacity-100'
                    : 'max-h-0 opacity-0'
                )}
              >
                <div className="ml-6 mt-1 space-y-0.5">
                  {module.lessons.map((lesson, lessonIndex) => {
                    const isCurrent = lesson.id === currentLessonId;
                    const isCompleted = isLessonCompleted(lesson.id);

                    return (
                      <Link
                        key={lesson.id}
                        to={`/lesson/${lesson.id}`}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm',
                          'transition-[background-color,transform] duration-200',
                          isCurrent
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'hover:bg-sidebar-accent/50 hover:translate-x-1',
                          isCompleted && 'text-git-orange'
                        )}
                        style={{ transitionDelay: `${lessonIndex * 30}ms` }}
                      >
                        {getLessonIcon(lesson.id, isCurrent)}
                        <span>{lesson.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Footer с управлением прогрессом */}
      <div className="border-t border-sidebar-border p-4">
        <details className="group">
          <summary className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <Settings className="h-4 w-4" />
            <span>Управление прогрессом</span>
            <ChevronDown className="ml-auto h-4 w-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3">
            <ProgressManager />
          </div>
        </details>
      </div>
    </aside>
  );
}
